import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  DVIRSearchCriteria,
  dvirsSelector,
  useSearchDVIRsQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {DVIRDataTable} from "../../components/data-table/DVIRDataTable/DVIRDataTable";
import {FromToDateToolbar} from "../../components/toolbar/FromToDateToolbar/FromToDateToolbar";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIDVIRSearchFilter} from "../../store/entity/dvir/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIDVIRSearchFilter} from "../../store/entity/dvir/actions";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {ErrorPage} from "../../components/page/ErrorPage/ErrorPage";
import {useAuth} from "../../providers/AuthProvider/AuthProvider";

export type DVIRSearchPageProps = {}

export const DVIRSearchPage = (props: DVIRSearchPageProps) => {
  const {regionId} = useApp();
  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const criteria = useOtsUISelector(selectUIDVIRSearchFilter)

  const selectDVIRs = useMemo(() => {
    return dvirsSelector();
  }, []);

  const searchArg: DVIRSearchCriteria | typeof skipToken = useMemo(() => {
    if (criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        region: regionId ? { scope: "SPECIFIC", values: [regionId] } : undefined,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId])

  const {dvirs, isFetching, refetch} = useSearchDVIRsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      dvirs: selectDVIRs(result),
      isFetching: result.isFetching,
    })
  });

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {
    if (fromDate.getTime() === criteria?.fromDttm && toDate?.getTime() === criteria.toDttm) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIDVIRSearchFilter({
        ...criteria,
        fromDttm: fromDate.getTime(),
        toDttm: toDate?.getTime()
      }))
    }
  }, [criteria, refetch, dispatch]);

  if (!hasAccess("dvir_view")) {
    return <ErrorPage message={t<string>("common.error.unauthorized", "Unauthorized")}/>
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.entity.dvir.search.title", "Search DVIRs")}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/dvirs/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : new Date(),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <DVIRDataTable
                  withBorder
                  records={dvirs}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
